import { isObject } from 'lodash';

import type { StudioExecutionColumnTypes } from '@blockworks/platform/api/research/studio';
import { Text, type TextProps } from '@blockworks/ui';

import { MarkdownRenderer } from '../markdown';

import { type D3FormatString, formatNumber, useFormatNumber, useFormatValue } from './utils';

type FormatValueProps = {
    children?: string | number;
    isMarkdown?: boolean;
    hasPositiveColor?: boolean;
    hasNegativeColor?: boolean;
    d3Format?: D3FormatString;
    type?: StudioExecutionColumnTypes;
    value: string | number | undefined;
} & Omit<TextProps<'span'>, 'children'>;

const FormatValue = ({
    hasNegativeColor,
    isMarkdown,
    hasPositiveColor,
    d3Format,
    type,
    value,
    ...rest
}: FormatValueProps) => {
    const { formattedValue, color } = useFormatValue({
        d3Format,
        value: isObject(value) ? JSON.stringify(value) : value,
        type,
        hasPositiveColor,
        hasNegativeColor,
    });

    if (!isMarkdown) {
        return (
            <Text as="span" color={color} {...rest}>
                {formattedValue}
            </Text>
        );
    }
    return (
        <Text as="span" color={color} {...rest}>
            <MarkdownRenderer.Memoized markdown={formattedValue?.toString() ?? ''} bodyFontSize="xs" />
        </Text>
    );
};

type FormatNumberProps = Pick<FormatValueProps, 'd3Format' | 'value'> & TextProps<'span'>;

const FormatNumber = ({ d3Format = ',', value, ...props }: FormatNumberProps) => {
    const formattedValue = useFormatNumber(value, d3Format);

    return (
        <Text as="span" {...props}>
            {formattedValue}
        </Text>
    );
};

FormatValue.Number = FormatNumber;
FormatValue.formatNumber = formatNumber;

export { FormatValue };
